import { Injectable, Injector } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppSettings } from '../../app.settings';
import { AuthService } from './auth.service';

/**
 * Browser tab title, as in the former apps: a route's own `title` (a translation key or text) when it has one,
 * otherwise the app name followed by the breadcrumbs, e.g. "Employee Self Service > Personal Data". Updated again
 * when the language changes.
 * TranslateService is resolved on first use: the router creates this strategy, and TranslateService's HTTP loader goes
 * through AppInterceptor, which needs the router.
 */
@Injectable()
export class AppTitleStrategy extends TitleStrategy {
  private lastSnapshot: RouterStateSnapshot;
  private translateService: TranslateService;

  constructor(private readonly title: Title,
              private readonly injector: Injector,
              private readonly appSettings: AppSettings) {
    super();
  }

  private get translate(): TranslateService {
    if (!this.translateService) {
      this.translateService = this.injector.get(TranslateService);
      this.translateService.onLangChange.subscribe(() => this.updateTitle(this.lastSnapshot));
    }
    return this.translateService;
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.lastSnapshot = snapshot;
    const routeTitle = this.buildTitle(snapshot);
    const isEss = AuthService.appForUrl(snapshot.url) === 'ess';
    const parts = routeTitle
      ? [routeTitle]
      : [isEss ? 'ess-title' : this.appSettings.settings.name, ...this.breadcrumbs(snapshot.root)];
    const texts = parts.map(part => this.translate.instant(part));
    // translation keys come back unchanged until the language is loaded; the language change sets the title then
    if (texts.some((text, i) => text === parts[i] && /^[a-z-]+(\.[a-z-]+)*$/.test(text))) {
      return;
    }
    this.title.setTitle(texts.join(' > '));
  }

  private breadcrumbs(node: ActivatedRouteSnapshot): string[] {
    const breadcrumb = node.data['breadcrumb'] && node.url.length ? [node.data['breadcrumb']] : [];
    return node.firstChild ? [...breadcrumb, ...this.breadcrumbs(node.firstChild)] : breadcrumb;
  }
}
